import Button from 'components/Basic/Button';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { setEnableFacetEdit } from 'store/slices/RoofEditSlice';
import { useRefs } from 'contexts/RefContext';
import { toggleManualToolModalState } from 'store/slices/ManualToolModal';
import { checkIfLayoutNeedsToBeReset, hasShadingAffectingTransform } from '../util';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { toggleRecalculateLayoutModalSliceState } from 'store/slices/RecalculateLayoutSlice';

import {
	resetAzimuthEditState, resetDeletedObstructionIDs, resetEditedObstructions, resetManualObs, setDeletedRoofIndexes,
	setEnableObsMode, setRoofIndexes, setThreeDState, setUserModificationState, toggleAddObstructionsMode, toggleDeleteFacetsMode, updateTransition
} from 'store/slices/ToolSlice';
import { setPanelMove, setAddPanel, setDeleteIndividualPanelsMode } from 'store/slices/PanelSlice';

import useCreateDeleteFacet from 'hooks/api/facet/useCreateDeleteFacet';
import useTranslatePanelPositionsMutation from 'hooks/api/facet/useTranslateFacet';
import useMovePanelsQuery from 'hooks/api/panel/movePanel';
import useRemovePanelsQuery from 'hooks/api/panel/removePanel';
import useAddPanelsQuery from 'hooks/api/panel/addPanel';
import useDeleteObstructionQuery from 'hooks/api/deleteObstruction';
import useEditFacetQuery from 'hooks/api/facet/uesEditFacet';
import useAddObstructionQuery from 'hooks/api/addObstruction';
import useScreenDims from 'hooks/useScreenDims';
import useRoofToolFacet from 'hooks/api/facet/useUpdateRoofTool';
import { enableRoofToolLines } from 'store/slices/RoofToolLinesSlice';
import useMutateGutterLines from 'hooks/api/facet/useUpdateGutteLines';
import { useMemo, useState } from 'react';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import { resetUndoStack } from 'store/slices/ObstructionUndoSlice';
import usePatchObstructionQuery from 'hooks/api/obstructions/patchObstruction';
import { selectActiveAction, TOOL_ACTION } from 'store/selectors/toolSelector';
import { useEditDSM } from 'hooks/api/editDsm/useEditDsm';
import { commitTreeEdits, setTreeDoneLoading, toogleTreeEditingMode } from 'store/slices/TreeSlice';
import { recalculateShading, FacetEditShadingStatus, latestFacetShadingStatus } from 'api/editDsm';
import { 
	applyDsmEditResult,
	shadingRecalcAccepted,
	shadingRecalcRequested,
	shadingFailed,
} from 'store/slices/DsmShadingSlice';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';
import { selectHasTreeEdits } from 'store/slices/TreeSlice/selector';
import { isInHouseSimulationMode, isModeDefault } from '../../../../util';

const MutationKeys = [
	'create-new-facets', 'add-panels', 'remove-panels', 'move-panels', 'translate-panels', 'delete-obstructions',
	'update-edited-facet', 'add-obstructions', 'update-roof-tool-facet', 'edit-gutter-lines'
];

export default function CreateBtn() {
	const [loading, setLoading] = useState(false);
	const { newlyCreatedFacets, deletedRoofIndexes, roofIndexes, deletedObstructionIDs, groundMountEnabled, anyInvalidAzimuth, manualObs, 
		deleteFacetMode, enableEditObs, editedObstructions, invalidObstructionIds } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { 
		newlyAddedPanels, deletedPanels, 
		currentMovablePanels, activePanelMode, tempPanelsGroup,
		isInvalidPanelOperation, selectedMovablePanels
	} = useSelector((state: RootState) => state.panelSlice.data);

	const { uuid, panel, mode, allRoofSegs, designType } = useSelector((state: RootState) => state.roofData.data);
	const { enableEditFacet, editableFacets, invalidFacetIDs } = useSelector((state: RootState) => state.roofEditData.data);

	const threeDModelEnabled = useSelector((state: RootState) => state.toolNewPostions.data.threeDModelEnabled);
	const activeAction = useSelector(selectActiveAction);
	const isTreeEditAction = activeAction === TOOL_ACTION.EDIT_TREE;
	const hasTreeEdits = useSelector(selectHasTreeEdits);

	const { activeRoofTool, createSpecialEdges, updateSpecialEdges, selectedFacets, effectedFacets } 
		= useSelector((state: RootState) => state.roofToolLines.data);
	const { 
		confirmed: hasDetectedEdgesConfirmed, 
		loading: edgeDetectionInProgress 
	} = useSelector((state: RootState) => state.roofToolLines.autoEdgeDetection);

	const needGutterLineUpdate = Object.keys(selectedFacets).some(
		eachFacet => selectedFacets[eachFacet].newAddedGutterLines.length > 0 || selectedFacets[eachFacet].removedGutterLineIds.length > 0);

	const defaultMode = isModeDefault(mode);
	const isInHouseMode = isInHouseSimulationMode(mode);
	const { konvaRef } = useRefs();
	const dispatch = useDispatch<AppDispatch>();
	const startShadingRecalc = useStartShadingRecalc();
	const showLoader = useQueriesRunningInBackGround(MutationKeys, 'mutating');

	const hasTransformedFacet = !!roofIndexes.length;

	const hasCreatedFacet = !!Object.keys(newlyCreatedFacets).length;
	const hasAddedManualObs = !!Object.keys(manualObs).length;
	const hasEditedObs = !!Object.keys(editedObstructions).length;
	const hasDeletedFacet = !!deletedRoofIndexes.length;
	const hasDeletedGroundMountFacet = useMemo(() => deletedRoofIndexes.some(
		id => allRoofSegs.find(s => s.id === id)?.isGroundMount), [deletedRoofIndexes, allRoofSegs]);
	const hasDeletedObstruction = !!deletedObstructionIDs.length;
	const isNewlyAddedFacetsGroundMountInDefaultMode = defaultMode && groundMountEnabled && hasCreatedFacet;

	const hasPanelsAdded = newlyAddedPanels.length > 0;
	const hasPanelsDeleted = deletedPanels.length > 0;
	const hasPanelsMoved = useMemo(() => currentMovablePanels.some(moved => {
		const original = allRoofSegs.find(s => s.id === moved.roofSegmentId)?.panels.find(p => p.id === moved.panelId);
		return JSON.stringify(moved.exteriorCoords) !== JSON.stringify(original?.exteriorCoords);
	}), [currentMovablePanels, allRoofSegs]);

	const editedValidFacetsCount = (editableFacets.reduce((acc, eachSeg) => acc + ((eachSeg.isEdited ) ? 1 : 0), 0)) > 0;

	const hasRoofToolEdits = createSpecialEdges.length > 0 || updateSpecialEdges.length > 0 || needGutterLineUpdate 
		|| hasDetectedEdgesConfirmed;

	// TODO: implemnet custom done btn
	const disableOnPartialFacet = !(activePanelMode || enableEditFacet || isTreeEditAction) && !hasTransformedFacet 
	&& !(hasCreatedFacet || hasDeletedFacet 
		|| hasDeletedObstruction || hasAddedManualObs || activeRoofTool || hasEditedObs);

	const disableOnPartialPanel = activePanelMode && 
		(!(hasPanelsAdded || hasPanelsDeleted || hasPanelsMoved) || isInvalidPanelOperation || selectedMovablePanels.length > 0);

	const disableOnPartialFacetEdit = enableEditFacet && !(editedValidFacetsCount) || invalidFacetIDs.length > 0;
	const disableOnPartialRoofToolEidt = activeRoofTool && (!(hasRoofToolEdits) || effectedFacets.length > 0 || edgeDetectionInProgress);
	
	const hasInvalidObstructions = invalidObstructionIds.length > 0;

	const disableDoneInTreeEditMode = isTreeEditAction && !hasTreeEdits;

	const disableDoneButton = !!showLoader || disableOnPartialFacet || disableOnPartialPanel || disableOnPartialFacetEdit 
		|| anyInvalidAzimuth || tempPanelsGroup.length > 0 
		|| disableOnPartialRoofToolEidt || hasInvalidObstructions || disableDoneInTreeEditMode;

	const createNewFacetsMutation = useCreateDeleteFacet();
	const translatePanelPositionsMutation = useTranslatePanelPositionsMutation();

	const updateFacetsEditQuery = useEditFacetQuery();

	const addNewPanelsQuery = useAddPanelsQuery();
	const removePanelsQuery = useRemovePanelsQuery();
	const movablePanelsQuery = useMovePanelsQuery();

	const pathObstructionQuery = usePatchObstructionQuery();
	const screenDims = useScreenDims();

	const updateRoofToolQuery = useRoofToolFacet();
	const updateGutteLinesQuery = useMutateGutterLines();

	const editDSMQuery = useEditDSM();

	const { width: screenWidth, height: screenHeight } = screenDims;
	const isMobileView = screenWidth < 860;
	const [showConfirm, setShowConfirm] = useState(false);

	function beforeHandleClick() {
		if (isTreeEditAction) {
			handleClick();
			return;
		}
		const showResetLayoutMessage = konvaRef?.current && checkIfLayoutNeedsToBeReset(konvaRef.current, roofIndexes, allRoofSegs);

		if (showResetLayoutMessage) {
			dispatch(toggleRecalculateLayoutModalSliceState({
				open: true,
				warningMessage: 'Roof pitch changes will recalculate the layout. Proceed?',
			}));
		} else {
			handleClick();
		}
	}

	function resetAllMOdifiedState() {
		dispatch(setPanelMove({ shouldEnablePanelMove: false }));
		dispatch(setAddPanel({ shouldEnable: false }));
		dispatch(setDeleteIndividualPanelsMode({ enabled: false }));

		dispatch(setEnableFacetEdit({ isEnable: false }));
		dispatch(toggleDeleteFacetsMode({ enable: false }));

		dispatch(setDeletedRoofIndexes({ deletedRoofIndexes: [] }));
		dispatch(setRoofIndexes({ roofIndexes: [] }));

		dispatch(resetDeletedObstructionIDs());
		dispatch(setUserModificationState({ hasMadeChanges: true }));
		dispatch(updateTransition({ currentTransitionState: false }));
		dispatch(toggleAddObstructionsMode({ enable: false }));
		dispatch(resetManualObs());
		dispatch(setEnableObsMode({enable:false}));
		dispatch(resetUndoStack());
		dispatch(enableRoofToolLines({ shouldEnable: false }));
		dispatch(resetEditedObstructions());

		// Reset : tree state
		dispatch(toogleTreeEditingMode(false));
		dispatch(setThreeDState({ enabled: false }));
	} 

	/**
	 * Kick off the background facet-shading recompute. `statuses` are the settled results of the facet
	 * mutations — each resolves with a FacetEditShadingStatus (the facet pair only, no dsmEdits): the
	 * accumulation, not the full bake, is what closes it. We keep the highest dsmVersion seen.
	 * facetEditListener takes over from here: 3s edit-facet/status poll → changePanel on completion.
	 */
	async function startFacetShadingRecompute(statuses: PromiseSettledResult<FacetEditShadingStatus | undefined>[]) {
		return startShadingRecalc(latestFacetShadingStatus(statuses));
	}

	async function handleDeleteConfirmation() {
		setShowConfirm(false);
		setLoading(true);
		// snapshot before the delete mutation + changePanel refresh clear deletedRoofIndexes
		const refreshShadingAfterFacetEdit = isInHouseMode && hasDeletedGroundMountFacet;
		const deleteResult = await Promise.allSettled([createNewFacetsMutation.mutateAsync()]);
		await dispatch(changePanel({ uuid, panelKey: panel.key, designType }));
		if (refreshShadingAfterFacetEdit) await startFacetShadingRecompute(deleteResult);
		dispatch(setDeletedRoofIndexes({ deletedRoofIndexes: [] }));
		dispatch(toggleDeleteFacetsMode({ enable: false }));
		setLoading(false);
	}

	async function handleClick() {
		try {
			const hasFacetCreatedInNonDefaultModes = !defaultMode && hasCreatedFacet;
			const showManualModal = isNewlyAddedFacetsGroundMountInDefaultMode || hasFacetCreatedInNonDefaultModes;
			if (showManualModal) {
				dispatch(toggleManualToolModalState({ value: true }));
				return;
			}
			
			if(deleteFacetMode) {
				setShowConfirm(true);
				return;
			}
			setLoading(true);

			// snapshot before the mutations + resetAllMOdifiedState clear the edit state
			const hasShadingAffectingFacetTransform = hasTransformedFacet && !!konvaRef?.current
				&& hasShadingAffectingTransform(konvaRef.current, roofIndexes, allRoofSegs);
			const refreshShadingAfterFacetEdit = isInHouseMode
				&& (hasCreatedFacet || enableEditFacet || hasShadingAffectingFacetTransform || hasDeletedFacet);

			const mutations = [];
			const facetEdits: Promise<FacetEditShadingStatus | undefined>[] = [];
			const panelEdits: Promise<FacetEditShadingStatus | undefined>[] = [];

			// facet create/delete must resolve before the other mutations below: a pending delete
			// can invalidate facet/panel ids they reference, so they must not fire while it's in flight.
			const facetCreateDeleteResults = (hasCreatedFacet || hasDeletedFacet)
				? await Promise.allSettled([createNewFacetsMutation.mutateAsync()])
				: [];

			if (hasTransformedFacet) facetEdits.push(translatePanelPositionsMutation.mutateAsync());

			if (hasPanelsAdded) panelEdits.push(addNewPanelsQuery.mutateAsync());
			if (hasPanelsDeleted) panelEdits.push(removePanelsQuery.mutateAsync());
			if (hasPanelsMoved) panelEdits.push(movablePanelsQuery.mutateAsync());
			if(enableEditObs) mutations.push(pathObstructionQuery.mutateAsync());

			const [facetTransformResults, panelShadingResults] = await Promise.all([
				Promise.allSettled(facetEdits),
				Promise.allSettled(panelEdits),
				Promise.allSettled(mutations),
			]);
			const facetShadingResults = [...facetCreateDeleteResults, ...facetTransformResults];

			// facet edit runs sequentially, after every other mutation has settled (edge tool depends on it)
			if (enableEditFacet) {
				facetShadingResults.push(
					...await Promise.allSettled([updateFacetsEditQuery.mutateAsync()]));
			}
			// TODO: seperate done callback

			if (createSpecialEdges.length > 0 || updateSpecialEdges.length > 0 ||
				hasDetectedEdgesConfirmed
			) { 
				await updateRoofToolQuery.mutateAsync();
			}
			if(needGutterLineUpdate) {
				await updateGutteLinesQuery.mutateAsync();
			}

			if(isTreeEditAction){
				dispatch(setTreeDoneLoading(true));
				try {
					// 1) flush the batched edits — saved server-side, NO recompute. Marks the design dirty.
					const editRes = await editDSMQuery.mutateAsync();
					dispatch(applyDsmEditResult(editRes));
					// must precede commitTreeEdits(), else shape flashes hidden
					await dispatch(changePanel({ uuid, panelKey: panel.key, designType, refetchIrradiance: false, retainPanelSelectionOnUi: true }));
					dispatch(commitTreeEdits());
					dispatch(shadingRecalcRequested()); 
					// 2) start the async recompute. dsmShadingListener (middleware) takes over from here:
					//    polls the status, refetches the design on completion, and clears "recomputing".
					dispatch(shadingRecalcAccepted(await recalculateShading(uuid)));
					dispatch(toogleTreeEditingMode(false));
					if (threeDModelEnabled) {
						dispatch(setThreeDState({ enabled: false }));
					}
				} catch {
					dispatch(shadingFailed({ message: 'Could not start shading recompute' }));
				} finally {
					dispatch(setTreeDoneLoading(false));
				}
				return;
			}
			await dispatch(changePanel({ uuid, panelKey: panel.key, designType }));
			dispatch(resetAzimuthEditState());
			resetAllMOdifiedState();
			// One recompute for the whole batch: the facet claims (in-house, any facet edit) plus the
			// panel claims. latestFacetShadingStatus keeps the highest dsmVersion and yields
			// undefined — a no-op — when nothing is owed.
			startFacetShadingRecompute([
				...(refreshShadingAfterFacetEdit ? facetShadingResults : []),
				...panelShadingResults,
			]);

		} finally {
			setLoading(false);
		}
	}

	function handleDoneCancel(){
		setShowConfirm(false);
		resetAllMOdifiedState();
	}

	return (
		<>
			<Button
				onClick={beforeHandleClick}
				style={{ background: 'var(--primary)', color: 'var(--btnLabel)' }}
				showLoader={showLoader || loading}
				disabled={disableDoneButton || loading}
				className={`${anyInvalidAzimuth ? 'azimuthInvalid' : ''} loaderbefore close-icon btn-done`}
				id="done"
			>
				<svg
					width={isMobileView ? '19' : '22'}
					height={isMobileView ? '19' : '22.5'}
					viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M8.94727 10.2716L13.2626 13.5088C13.53 13.7093 13.8632 13.8014 14.1956 13.7665C14.528 13.7316 14.8348 
				13.5724 15.0547 13.3207L23.5172 3.64893" stroke="#fff" strokeWidth="1.69284" strokeLinecap="round" />
					<path d="M24.8418 12.9207C24.8418 15.4115 24.0617 17.8398 22.6108 19.8645C21.16 21.8892 19.1114 23.4085 16.7527 
				24.2092C14.394 25.0098 11.8438 25.0515 9.46027 24.3284C7.07669 23.6053 4.97949 22.1538 3.46322 20.1776C1.94695 
				18.2015 1.08777 15.8 1.00637 13.3105C0.924968 10.821 1.62543 8.3685 3.00936 6.29751C4.3933 4.22652 6.3912 2.64106 
				8.72244 1.7638C11.0537 0.886549 13.6012 0.761569 16.0071 1.40642" stroke="#fff" strokeWidth="1.69284" strokeLinecap="round" />
				</svg>
				<label>Done</label>
			</Button>

			<ConfirmationModal
				open={showConfirm}
				message={'Are you sure you want to delete the selected facets?'}
				onConfirm={handleDeleteConfirmation}
				onCancel={handleDoneCancel}
			/>
		</>
	);
}
